/**
 * @file product/index.ts
 * @description Handles product services.
 *
 * @author smedonca
 *
 */

/**
 * Imports.
 */
import * as _cache from '../cache';
import { CachedQuotes } from '../quotes/model';
import Product, { ProductCompare, ProductSetting } from '../../functions/product/product.model';
import products from '../../content/products.json';
import { ProductContent } from '../../components/ProductCard/model';

/**
 * Reads the cached quotes. must be called only after a request is done
 * @returns the quotes
 */
const getQuotesFromCache = async (): Promise<CachedQuotes> => {
  const quotes = _cache.getItem<CachedQuotes>(_cache.Key.quotesCache);
  return quotes;
};

/**
 * Updates product cache.
 * @param product Product data.
 */
export const updateProduct = async (product: Product): Promise<void> => {
  const productCache = _cache.getItem<Product>(_cache.Key.product);
  const quotes = await getQuotesFromCache();
  const program = quotes.response.programs.find((p) => p.Value === product.contractCode);
  _cache.setItem<Product>(_cache.Key.product, {
    ...productCache,
    ...product,
    category: program?.Type,
    programPrefix: program?.Prefix
  });
};

/**
 * Gets cached product.
 * @returns Cached product.
 */
export const getProduct = (): Product => {
  return _cache.getItem<Product>(_cache.Key.product);
};

/**
 * Gets cached product information
 * @returns Cached products.
 */
export const getProducts = async (programId?: string): Promise<ProductSetting[]> => {
  const quotes = await getQuotesFromCache();
  const programs = quotes.response.programs;
  const selProd = programId ?? quotes.response.programId;

  let result = [] as ProductSetting[];
  // mapped the results to the product configuration
  if (programs && programs.length > 0) {
    result = programs.map((item) => {
      const covCode = item.Preferred?.Code;
      const planCode = item.Preferred?.Plan;
      const details = item.Coverages?.find(i => i['@Code'] === covCode)?.Plans.find(p => p['@Name'] === planCode);
      return {
        selected: (selProd === item.ProgramId),
        id: item.ProgramId,
        coverage: covCode ?? '',
        amount: details?.Deductible.RetailCost ?? '0',
        pos: item.ProgramId === quotes.response.programId ? 0 : 1
      };
    });
  }
  return result;
};

/**
 * Get a list of products with the same plan
 * @param compare the plan parameters that should be used to find the comparable plans
 * @returns the product list with similar plans
 */
export const getComparables = async (compare: ProductCompare): Promise<ProductSetting[]> => {
  const quotes = await getQuotesFromCache();
  const programs = quotes.response.programs;

  let result = [] as ProductSetting[];
  // mapped the results to the product configuration
  if (programs && programs.length > 0) {
    result = programs.map((item) => {
      const covCode = compare.code;
      const deductible = compare.deductible;
      const months = compare.months;
      const miles = compare.miles;
      const details = item.Coverages?.find(i => i['@Code'] === covCode)?.Plans.filter(p => p['@TMiles'] === miles && p['@TMonths'] === months)?.find(d => d.Deductible['@Sur'] === deductible);
      return {
        selected: (compare.programId === item.ProgramId),
        id: item.ProgramId,
        coverage: covCode ?? '',
        amount: details?.Deductible.RetailCost ?? '0',
        pos: item.ProgramId === quotes.response.programId ? 0 : 1
      };
    });
  }
  return result;
};

/**
 * clear product cache
 */
export const clearProduct = async (): Promise<void> => {
  _cache.removeItem(_cache.Key.product);
};

/**
 * get short name of selected product
 */
export const getSelectedProductShortname = (selectedProgram?: string): string => {
  if (!selectedProgram) {
    const productCache = getProduct();
    selectedProgram = productCache?.contractCode;
  }

  if (selectedProgram) {
    return (products as unknown as ProductContent).content[selectedProgram].shortname ?? '';
  }

  return '';
};
